import { isProblemDetail } from "@roastery/api-client";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Turns an API error into something a person can read: the domain `code` of an RFC 9457 problem maps
 * to `errors.<CODE>` in the translations, unknown codes fall back to the server's detail text, and
 * anything else to a generic message.
 *
 * <p>It lives here rather than in one application because both frontends need it and the server
 * speaks one language. A wholesale customer reading Polish should not be handed the English sentence
 * the backend wrote for a log.
 *
 * <p>The problem's own members are handed to the translation as interpolation values, so a message
 * can say why and not only what: `errors.TENANT_SUSPENDED` reads `{{suspensionReason}}` straight off
 * the 423 the server sent (US-14.7). A message that names nothing is unaffected.
 */
export function useApiErrorMessage() {
  const { t } = useTranslation();
  return useCallback(
    (error: unknown): string => {
      if (isProblemDetail(error)) {
        const key = error.code ? `errors.${error.code}` : "";
        const fallback = error.detail ?? error.title ?? t("errors.generic");
        return key ? t(key, { ...error, defaultValue: fallback }) : fallback;
      }
      if (error instanceof Error && error.message) {
        return error.message;
      }
      return t("errors.generic");
    },
    [t],
  );
}

/**
 * Holds the failure of a dialog's action, translated, so the dialog can show it **inside itself**
 * (US-37.4).
 *
 * <p>Until now every dialog answered a 4xx with a toast in the bottom-right corner — which, with a
 * dialog open on a laptop screen, is behind the dialog. Somebody presses "Save", nothing appears to
 * happen, and the reason why is off-screen (UX-05, T-10). The toast stays for success, where it is
 * exactly right: the dialog is gone by then and the message is the only trace left.
 *
 * <p>It lives beside {@link useApiErrorMessage} for the reason that hook gives about itself: all
 * three applications have dialogs and the server speaks one language. What it keeps is a **message**,
 * not the error — the translation happens once, here, so no screen has to remember to do it.
 *
 * <p>`open` clears the message when the dialog is opened again: a failure from last time is not a
 * failure of this time. The comparison happens during render rather than in an effect, because that
 * is what the React Compiler asks for and because an effect would paint the stale message for a
 * frame. A dialog that is unmounted while closed can simply pass `true` — its state goes with it.
 *
 * <p>A retry keeps the old message until the new answer arrives. That is deliberate: the button says
 * "Saving…" in the meantime, and a box that empties itself and then fills again reads as a flicker.
 */
export function useDialogError(open: boolean) {
  const message = useApiErrorMessage();
  const [failure, setFailure] = useState<string | null>(null);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open && failure !== null) {
      setFailure(null);
    }
  }
  const onError = useCallback((error: unknown) => setFailure(message(error)), [message]);
  // `show` is for the refusals the dialog makes itself — "a serial number is required" — which used
  // the same toast and were just as easy to miss. They are already sentences, so they skip the
  // translation of a problem document.
  const show = useCallback((sentence: string) => setFailure(sentence), []);
  const clear = useCallback(() => setFailure(null), []);
  return { error: failure, onError, show, clear };
}
